<template>
    <CqModal
        :showModal="show"
        :preventClosing="isSaving"
        @closeModal="closeModal"
        heading="Stempel aanmaken"
    >
        <div v-if="resourceHasFutureStamps">
            <p class="warning">Let op, {{ resourceDisplayName }} heeft al stempels in de toekomst.<br />
                Deze stempel zal doorlopen tot de begin datum van de toekomstige stempel</p>
        </div>
        <CqTextInput type="number" :label="'Aantal dagen voor stempel (' + minimumDays + '-' + maximumDays + ')'" v-model="value" @update:modelValue="onChangeValue" :invalid="error" :invalidFeedback="errorLabel" />
        <CqButtonBar>
            <CqButton label="Maak stempel" :disabled="isSaving || !valueIsValid()" primary @click="save" />
        </CqButtonBar>
    </CqModal>
</template>

<script lang="ts">
    import {CqButton, CqButtonBar, CqModal, CqTextInput} from '@cquential/ui';
    import {ApiClient, PersonalStamp} from '@cquential/api';
    import Notify from '@/components/modals/Notify.vue';
    import {ResourceUtil} from '@/util/ResourceUtil';
    import {ResourceWithKeyedByDateObjects} from '@/types/api-object-extensions';
    import {Component, Vue} from 'vue-facing-decorator';

    @Component({
        components: {
            CqButton,
            CqButtonBar,
            CqModal,
            CqTextInput,
        },
        emits: [
            'reload:stamp',
        ],
    })
    export default class PersonalStampIterationModal extends Vue {
        private static instance: PersonalStampIterationModal | null = null;

        private show: boolean = false;
        private value: number = 7;
        private error: boolean = false;
        private errorLabel: string = '';
        private minimumDays: number = 1;
        private maximumDays: number = 364;
        private resource: ResourceWithKeyedByDateObjects | null = null;
        private beginDate: Date = new Date();
        public isSaving: boolean = false;

        public get resourceDisplayName(): string {
            return this.resource ? ResourceUtil.displayName(this.resource) : '';
        }

        public mounted(): void {
            PersonalStampIterationModal.instance = this;
        }

        public beforeUnmount(): void {
            PersonalStampIterationModal.instance = null;
        }

        public get resourceHasFutureStamps(): boolean {
            let hasFutureStamps: boolean = false;
            if (this.resource && this.resource.personalStamps) {
                this.resource.personalStamps.forEach((personalStamp: PersonalStamp): void => {
                    if (personalStamp.beginDate > this.beginDate) {
                        hasFutureStamps = true;
                    }
                });
            }

            return hasFutureStamps;
        }

        public onChangeValue(): void {
            this.error = !this.valueIsValid();
        }

        public valueIsValid(): boolean {
            let returnValue: boolean = true;
            if (this.value === null) {
                this.errorLabel = 'Vul een waarde in';
                returnValue = false;
            } else if (this.value < this.minimumDays) {
                this.errorLabel = 'Aantal dagen is onder het minimum';
                returnValue = false;
            } else if (this.value > this.maximumDays) {
                this.errorLabel = 'Aantal dagen is boven het maximum';
                returnValue = false;
            }

            return returnValue;
        }

        public save(): void {
            if (this.isSaving) {
                return;
            }

            if (!this.valueIsValid()) {
                this.closeModal();

                return;
            }

            const personalStamp: PersonalStamp = {
                resourceId: this.resource!.id,
                beginDate: this.beginDate,
                endDate: null,
                iterationLength: this.value,
            };

            this.isSaving = true;
            ApiClient.instance.saveNewPersonalStamp(personalStamp)
                .then((): void => {
                    this.$emit('reload:stamp', this.resource!.id!);
                    this.closeModal();
                })
                .catch((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Er is iets fout gegaan. Ververs het planbord.', type: 'error'});
                }).finally((): void => {
                    this.isSaving = false;
                });
        }

        public closeModal(): void {
            this.show = false;
        }

        public static open(resource: ResourceWithKeyedByDateObjects, date: Date): void {
            this.instance!.resource = resource;
            this.instance!.beginDate = date;
            this.instance!.show = true;
        }
    }
</script>

<style lang="scss" scoped>
    .warning {
        color: #f4511e;
    }
</style>
